import React from 'react';
import { Award, Star } from 'lucide-react';

const Recognition = () => {
  return (
    <section className="py-24 bg-gradient-to-br from-slate-50 to-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold mb-4 flex items-center gap-2">
            <Award size={16} />
            Recognition & Credentials
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Trusted by Leading AMCs
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Our commitment to excellence has been recognized by India's top mutual fund houses
          </p>
        </div>

        {/* Rewards Section */}
        <div className="mb-20">
          <div className="relative max-w-6xl mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-2 border-slate-100">
              <div className="bg-gradient-to-r from-amber-600 to-amber-700 py-6 px-8">
                <h3 className="text-3xl font-bold text-white text-center flex items-center justify-center gap-3">
                  <Star className="text-white" fill="white" size={32} />
                  Rewards & Recognition
                  <Star className="text-white" fill="white" size={32} />
                </h3>
              </div>
              <div className="p-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_rubicon-funds/artifacts/qo6hug8a_Screenshot%202026-04-05%20at%205.08.20%E2%80%AFPM.png"
                  alt="Rewards and Recognition from leading AMCs"
                  className="w-full rounded-xl shadow-lg"
                />
                <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-amber-50 rounded-lg">
                    <p className="text-2xl font-bold text-amber-700">25+</p>
                    <p className="text-sm text-slate-600">AMC Awards</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-700">Top</p>
                    <p className="text-sm text-slate-600">Distributor</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-700">17+</p>
                    <p className="text-sm text-slate-600">Years Service</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Founder's Quote & Credentials */}
        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Quote Card */}
          <div className="bg-gradient-to-br from-blue-600 to-purple-700 rounded-3xl shadow-2xl overflow-hidden">
            <div className="p-8 md:p-12">
              <div className="mb-6">
                <svg className="w-12 h-12 text-white opacity-50" fill="currentColor" viewBox="0 0 32 32">
                  <path d="M10 8c-3.3 0-6 2.7-6 6v10h10V14h-6c0-2.2 1.8-4 4-4V8zm16 0c-3.3 0-6 2.7-6 6v10h10V14h-6c0-2.2 1.8-4 4-4V8z"/>
                </svg>
              </div>
              <blockquote className="text-xl md:text-2xl font-bold text-white leading-tight mb-8">
                "Mutual funds are all you need; there's no need to look beyond for financial success."
              </blockquote>
              <div className="flex items-center gap-6">
                <div className="w-32 h-32 rounded-full bg-white/20 overflow-hidden flex-shrink-0 border-4 border-white/30">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_rubicon-funds/artifacts/z67x5csk_Screenshot%202026-04-05%20at%205.07.54%E2%80%AFPM.png"
                    alt="Rohit Jain Bhansali"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div>
                  <p className="text-xl font-bold text-white">Rohit Jain Bhansali</p>
                  <p className="text-white/90 text-base">Founder & Principal Advisor</p>
                </div>
              </div>
            </div>
          </div>

          {/* Professional Credentials Card */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-2 border-slate-100">
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 py-6 px-8">
              <h3 className="text-2xl font-bold text-white text-center">Professional Qualification</h3>
            </div>
            <div className="p-8">
              <div className="aspect-square rounded-xl overflow-hidden shadow-lg border-2 border-slate-200">
                <img 
                  src="https://customer-assets.emergentagent.com/job_rubicon-funds/artifacts/r78q3hu3_Screenshot%202026-04-05%20at%205.08.07%E2%80%AFPM.png"
                  alt="ICSI Final Examination Certificate - Rohit Jain"
                  className="w-full h-full object-contain bg-slate-50"
                />
              </div>
              <div className="mt-6 text-center">
                <p className="text-lg font-bold text-slate-900">Company Secretary (CS)</p>
                <p className="text-slate-600">Institute of Company Secretaries of India</p>
                <p className="text-sm text-slate-500 mt-2">June 2009 | Kolkata</p>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-20 max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-2xl p-8 border-2 border-amber-200">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <p className="text-sm text-amber-700 font-semibold mb-1">Recognized By</p>
                <p className="text-lg font-bold text-slate-900">Top 20 AMCs</p>
              </div>
              <div className="border-l-2 border-r-2 border-amber-200 md:border-l-2 md:border-r-2">
                <p className="text-sm text-amber-700 font-semibold mb-1">Professional</p>
                <p className="text-lg font-bold text-slate-900">CS Qualified</p>
              </div>
              <div>
                <p className="text-sm text-amber-700 font-semibold mb-1">Client Trust</p>
                <p className="text-lg font-bold text-slate-900">500+ Families</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Recognition;
